import { useState, useEffect, useRef } from "react";
import { NavLink, useHistory } from "react-router-dom";
import { auth } from "../FIREBASE/firebase";
import routeUrls from "../COMPONENTS/navbar/navLink";
import { TrainingData, CalendarEvent, WellnessDay, Activity } from "../types/training";
import { WeeklyTSSChart } from "../COMPONENTS/WeeklyTSSChart";
import { WorkoutChart } from "../COMPONENTS/WorkoutChart";
import { PolarizationChart } from "../COMPONENTS/PolarizationChart";
import { TrainingInfo } from "../COMPONENTS/TrainingInfo";
import { loadPlanWeekMeta, isoWeekKey } from "../COMPONENTS/planWeekMeta";
import {
  SavedExercise, NewEventPayload, loadSavedExercises, addSavedExercise,
  removeSavedExercise, exerciseToEventPayload,
} from "../COMPONENTS/savedExercises";
import "../CSS/training.scss";
import { usePageBackground } from "../COMPONENTS/usePageBackground";

const PHASE_LABELS: Record<string, string> = {
  base: "Base", build: "Build", cutback: "Cutback", peak: "Peak", taper: "Taper", race: "Race",
};

// Decimal hours → "8h" / "7h45m" / "45m".
function fmtHours(h: number): string {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return hh > 0 ? (mm > 0 ? `${hh}h${mm}m` : `${hh}h`) : `${mm}m`;
}

const ACTIVITY_ICONS: Record<string, string> = {
  Ride: "🚴", VirtualRide: "🚴", Run: "🏃", NordicSki: "⛷️",
  Swim: "🏊", Walk: "🚶", Hike: "🥾", WeightTraining: "🏋️",
  Workout: "💪", Yoga: "🧘", Rowing: "🚣",
};

function icon(type: string) { return ACTIVITY_ICONS[type] ?? "🏅"; }

function fmtDuration(secs: number) {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmtDist(meters: number | null) {
  if (!meters) return null;
  return meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`;
}

// ISO week number for a "YYYY-MM-DD" date.
function isoWeekNum(dateStr: string): number {
  const d = new Date(dateStr + "T12:00:00");
  const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  return Math.ceil(((utc.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

// Actual weekly metrics from completed activities: total hours, hard share (time
// at ~Z4+, avg HR ≥ 90% LTHR — same HR-zone basis the day analysis uses).
function weeklyMetrics(weekActivities: Activity[]): { secs: number; tss: number; hardPct: number | null } {
  let secs = 0, hardSecs = 0, tss = 0;
  for (const a of weekActivities) {
    const dur = a.moving_time || 0;
    secs += dur;
    tss += a.icu_training_load ?? 0;
    const lthr = a.icu_lthr ?? 177;
    if (a.average_heartrate && (a.average_heartrate / lthr) * 100 >= 90) hardSecs += dur;
  }
  return { secs, tss: Math.round(tss), hardPct: secs > 0 ? Math.round((hardSecs / secs) * 100) : null };
}

// ── Race detection ────────────────────────────────────────────────────────────

const RACE_KEYWORDS = ["loppet", "tävling", "marathon", "maraton", "birkebeiner", "vasaloppet"];

function isRaceEvent(event: CalendarEvent): boolean {
  const cat = event.category ?? "";
  if (/^RACE/i.test(cat)) return true;   // RACE / RACE_A / RACE_B / RACE_C …
  if (cat === "WORKOUT") return false;   // a planned/done training session is never a race (e.g. a long run)
  // Only fall back to name/description keywords for uncategorised events.
  const text = (event.name + " " + (event.description ?? "")).toLowerCase();
  if (RACE_KEYWORDS.some(k => text.includes(k))) return true;
  if (/(?<![a-z-])race(?![a-z-])/.test(text)) return true;
  if (/(?<![a-z-])lopp(?![a-z-])/.test(text)) return true;
  return false;
}

// ── Planning logic ────────────────────────────────────────────────────────────

const INTENSE_KEYWORDS = [
  "intervall", "interval", "tempo", "tröskel", "threshold",
  "z4", "z5", "sprint", "backe", "fartlek", "hård", "hard", "kvalitet",
];
const EASY_OVERRIDE = [
  "pendling", "commute", "lätt", "easy", "lugn", "vila", "rest",
  "lunch löp", "lunchlöp", "off",
];

function isIntense(event: CalendarEvent): boolean {
  const text = (event.name + " " + (event.description ?? "")).toLowerCase();
  if (EASY_OVERRIDE.some(k => text.includes(k))) return false;
  return INTENSE_KEYWORDS.some(k => text.includes(k));
}

function daysFromNow(isoDate: string): number {
  const date = new Date(isoDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

type Verdict = "keep" | "move" | "remove";
interface EventAdvice { verdict: Verdict; reason: string; }

function computeAdvice(event: CalendarEvent, tsb: number, intenseCount: number, intenseIndex: number): EventAdvice {
  if (!isIntense(event)) return { verdict: "keep", reason: "Baspass — alltid ok." };

  if (tsb < -30) return {
    verdict: "remove",
    reason: `Djup trötthet (Form ${Math.round(tsb)}). Kroppen behöver vila.`,
  };
  if (tsb < -20) {
    if (daysFromNow(event.start_date_local) <= 3) return {
      verdict: "remove",
      reason: `Hög trötthet (Form ${Math.round(tsb)}) och passet är inom 3 dagar.`,
    };
    return {
      verdict: "move",
      reason: `Hög trötthet (Form ${Math.round(tsb)}). Flytta till senare i veckan.`,
    };
  }
  if (intenseCount > 2 && intenseIndex >= 2) return {
    verdict: "move",
    reason: `Redan ${intenseCount} intensiva pass planerade den här veckan.`,
  };
  return { verdict: "keep", reason: `Formen räcker till (Form ${Math.round(tsb)}).` };
}

// ── Day comment generator ─────────────────────────────────────────────────────

function generateDayComment(activities: Activity[], wellness: WellnessDay | null): string {
  const totalLoad = activities.reduce((sum, a) => sum + (a.icu_training_load ?? 0), 0);
  const main = activities.reduce((best, a) =>
    (a.icu_training_load ?? 0) >= (best.icu_training_load ?? 0) ? a : best, activities[0]);

  const lthr = main.icu_lthr ?? 177;
  const ftp  = main.icu_ftp  ?? 250;
  const ctl  = main.icu_ctl;
  const atl  = main.icu_atl;
  const tsb  = ctl != null && atl != null ? Math.round(ctl - atl) : null;
  const hrv  = wellness?.hrv ?? null;
  const rhr  = wellness?.restingHR ?? null;

  const parts: string[] = [];

  // Session count + total load
  parts.push(`${activities.length === 1 ? "1 pass" : `${activities.length} pass`}, total belastning ${totalLoad}.`);

  // Form context
  if (tsb != null) {
    const tsbStr = tsb >= 0 ? `+${tsb}` : String(tsb);
    const label  = tsb < -30 ? "djup trötthet"
      : tsb < -20 ? "hög trötthet"
      : tsb < -10 ? "lätt negativ form"
      : tsb < 5   ? "balanserat"
      : "utvilad";
    parts.push(`Form ${tsbStr} (${label}).`);
  }

  // Morning readiness
  if (hrv != null && rhr != null) {
    if (hrv < 40 || rhr > 62) {
      parts.push(`Morgonindikatorer: HRV ${hrv}, vilopuls ${rhr} — trötthetssignaler.`);
    } else {
      parts.push(`HRV ${hrv}, vilopuls ${rhr}.`);
    }
  }

  // Main session breakdown
  const mainParts: string[] = [];
  const avgHR = main.average_heartrate;
  if (avgHR) {
    const pct  = Math.round((avgHR / lthr) * 100);
    const zone = pct < 70 ? "Z1" : pct < 80 ? "Z2" : pct < 87 ? "Z2–Z3" : pct < 93 ? "Z3–Z4" : pct < 100 ? "Z4" : "Z5+";
    mainParts.push(`snittpuls ${avgHR} bpm (${pct}% LTHR, ${zone})`);
  }
  const np  = main.icu_weighted_avg_watts;
  const dec = main.icu_decoupling;
  if (np != null && ["Ride", "VirtualRide"].includes(main.type)) {
    mainParts.push(`NP ${Math.round(np)}W (${Math.round((np / ftp) * 100)}% FTP)`);
  }
  if (dec != null && !isNaN(dec) && Math.abs(dec) > 5) {
    mainParts.push(`kopplingsfaktor ${dec.toFixed(1)}% — hjärtdrift`);
  }
  if (main.type === "Run" && (main.distance ?? 0) > 12000 && avgHR && avgHR / lthr > 0.82) {
    mainParts.push(`intensitet hög för långpass (opt. ${Math.round(lthr * 0.65)}–${Math.round(lthr * 0.75)} bpm)`);
  }
  if (mainParts.length > 0) {
    parts.push(`Huvudpass ${main.name}: ${mainParts.join(", ")}.`);
  }

  // Other sessions
  const others = activities.filter(a => a !== main);
  if (others.length > 0) {
    parts.push(`Övriga: ${others.map(a => a.name).join(", ")}.`);
  }

  return parts.join(" ");
}

// ── Metric chart ──────────────────────────────────────────────────────────────

interface MetricChartProps {
  label: string;
  tooltipTitle: string;
  tooltipText: string;
  values: (number | null)[];
  dates: string[];
  signed?: boolean;
  colorize?: boolean;
}

function MetricChart({ label, tooltipTitle, tooltipText, values, dates, signed = false, colorize = false }: MetricChartProps) {
  const VW = 300, VH = 110;
  const xL = 10, xR = 290;
  const yT = 24, yB = 82;

  const pts = values
    .map((v, i) => v !== null ? { i, v } : null)
    .filter((p): p is { i: number; v: number } => p !== null);

  const current = pts.length > 0 ? pts[pts.length - 1].v : null;
  const currentStr = current !== null
    ? (signed && current > 0 ? `+${Math.round(current)}` : String(Math.round(current)))
    : "—";
  const currentColor = colorize && current !== null
    ? (current >= 0 ? "#1f7a4d" : "#c2374f")
    : undefined;

  if (pts.length < 2) {
    return (
      <div className="metric-chart" tabIndex={0}>
        <div className="metric-chart__header">
          <span className="metric-chart__label">{label}</span>
          <span className="metric-chart__current" style={currentColor ? { color: currentColor } : undefined}>{currentStr}</span>
        </div>
        <div className="metric-chart__tooltip"><strong>{tooltipTitle}</strong><p>{tooltipText}</p></div>
      </div>
    );
  }

  const vs = pts.map(p => p.v);
  const vMin = Math.min(...vs);
  const vMax = Math.max(...vs);
  const vRange = vMax - vMin || 1;
  const iRange = values.length - 1 || 1;

  const cx = (i: number) => xL + (i / iRange) * (xR - xL);
  const cy = (v: number) => yB - ((v - vMin) / vRange) * (yB - yT);
  const linePts = pts.map(p => `${cx(p.i).toFixed(1)},${cy(p.v).toFixed(1)}`).join(" ");

  const fmtVal = (v: number) => { const r = Math.round(v); return signed && r > 0 ? `+${r}` : String(r); };
  const fmtAxisDate = (iso: string) => { const d = new Date(iso); return `${d.getDate()}/${d.getMonth() + 1}`; };

  return (
    <div className="metric-chart" tabIndex={0}>
      <div className="metric-chart__header">
        <span className="metric-chart__label">{label}</span>
        <span className="metric-chart__current" style={currentColor ? { color: currentColor } : undefined}>{currentStr}</span>
      </div>
      <svg viewBox={`0 0 ${VW} ${VH}`} className="metric-chart__svg">
        <line x1={xL} y1={yB} x2={xR} y2={yB} stroke="rgba(26,26,26,0.07)" strokeWidth="1" />
        <polyline points={linePts} fill="none" stroke="rgba(26,26,26,0.3)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        {values.map((v, i) => {
          if (v === null) return null;
          const x = cx(i); const y = cy(v);
          const labelY = Math.max(y - 8, 12);
          const isLast = i === values.length - 1;
          return (
            <g key={i}>
              <circle cx={x} cy={y} r={isLast ? 3.5 : 2.5} fill={isLast ? "rgba(26,26,26,0.9)" : "rgba(26,26,26,0.55)"} />
              <text x={x} y={labelY} textAnchor="middle" fontSize="8" fill={isLast ? "rgba(26,26,26,0.85)" : "rgba(26,26,26,0.4)"} fontWeight={isLast ? "700" : "400"} fontFamily="inherit">{fmtVal(v)}</text>
              {i % 2 === 0 && dates[i] && (
                <text x={x} y={VH - 1} textAnchor="middle" fontSize="7" fill="rgba(26,26,26,0.22)" fontFamily="inherit">{fmtAxisDate(dates[i])}</text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="metric-chart__tooltip">
        <strong>{tooltipTitle}</strong>
        <p>{tooltipText}</p>
      </div>
    </div>
  );
}

// ── Calendar ──────────────────────────────────────────────────────────────────

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const VERDICT_LABEL: Record<Verdict, string> = { keep: "Behåll", move: "Flytta", remove: "Ta bort" };

function EventModal({ event, advice, onClose, onDelete, onEditDescription }: {
  event: CalendarEvent;
  advice: EventAdvice | null;
  onClose: () => void;
  onDelete: (id: number) => Promise<void>;
  onEditDescription: (id: number, description: string) => Promise<void>;
}) {
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(event.description ?? "");
  const [saving, setSaving] = useState(false);
  const date = new Date(event.start_date_local).toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long" });

  async function handleDelete() {
    setDeleting(true);
    try { await onDelete(event.id); onClose(); } finally { setDeleting(false); }
  }
  async function handleSave() {
    setSaving(true);
    try { await onEditDescription(event.id, draft); setEditing(false); } finally { setSaving(false); }
  }

  return (
    <div className="cal__overlay" onClick={onClose}>
      <div className="cal__modal" onClick={e => e.stopPropagation()}>
        <button className="cal__modal-close" onClick={onClose}>✕</button>
        <p className="cal__modal-date">{date}</p>
        <h3 className="cal__modal-title">{event.name}</h3>
        <WorkoutChart
          description={editing ? draft : (event.description ?? "")}
          durationHours={(event as any).duration > 0 ? (event as any).duration / 3600 : 1}
          name={event.name}
        />
        {editing ? (
          <div className="cal__modal-edit">
            <textarea
              className="cal__modal-edit-area"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              rows={Math.min(14, Math.max(4, draft.split("\n").length + 1))}
            />
            <p className="cal__modal-edit-hint">
              Lägg till <b>Press lap</b> sist på en rad för att avsluta steget med lap-knappen
              på klockan (t.ex. <code>10min Z2 HR Press lap</code> för uppvärmning, eller
              <code>- 90s Z1 HR Press lap</code> för vila mellan intervaller).
            </p>
            <div className="cal__modal-edit-actions">
              <button className="cal__modal-edit-save" onClick={handleSave} disabled={saving}>
                {saving ? "Sparar…" : "Spara till intervals.icu"}
              </button>
              <button className="cal__modal-edit-cancel" onClick={() => { setDraft(event.description ?? ""); setEditing(false); }}>
                Avbryt
              </button>
            </div>
          </div>
        ) : (
          <>
            {event.description && <div className="cal__modal-desc">{event.description}</div>}
            <button className="cal__modal-edit-btn" onClick={() => { setDraft(event.description ?? ""); setEditing(true); }}>
              Redigera pass
            </button>
          </>
        )}
        {advice && (
          <div className={`cal__modal-advice cal__modal-advice--${advice.verdict}`}>
            <span className="cal__modal-advice-verdict">{VERDICT_LABEL[advice.verdict]}</span>
            <p className="cal__modal-advice-reason">{advice.reason}</p>
            {advice.verdict !== "keep" && (
              <button className="cal__modal-delete-btn" onClick={handleDelete} disabled={deleting}>
                {deleting ? "…" : "Ta bort pass"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type PostState = "idle" | "posting" | "done" | "error";

function ActivityModal({ activity, onClose }: {
  activity: Activity;
  onClose: () => void;
}) {
  const date = new Date(activity.start_date_local).toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long" });
  return (
    <div className="cal__overlay" onClick={onClose}>
      <div className="cal__modal" onClick={e => e.stopPropagation()}>
        <button className="cal__modal-close" onClick={onClose}>✕</button>
        <p className="cal__modal-date">{date}</p>
        <h3 className="cal__modal-title">{icon(activity.type)} {activity.name}</h3>
        <WorkoutChart
          description={(activity as any).description ?? activity.name ?? ""}
          durationHours={activity.moving_time / 3600}
          name={`${activity.type} ${activity.name ?? ""}`}
        />
        <div className="cal__modal-desc">
          {fmtDist(activity.distance) && <span>{fmtDist(activity.distance)}</span>}
          {fmtDist(activity.distance) && " · "}
          <span>{fmtDuration(activity.moving_time)}</span>
          {activity.icu_training_load != null && ` · Load ${activity.icu_training_load}`}
          {activity.average_heartrate != null && ` · ${activity.average_heartrate} bpm`}
        </div>
      </div>
    </div>
  );
}

function DayAnalysisModal({ dateStr, activities, wellness, onClose, onPost }: {
  dateStr: string;
  activities: Activity[];
  wellness: WellnessDay | null;
  onClose: () => void;
  onPost: (activityId: string, content: string) => Promise<void>;
}) {
  const [postState, setPostState] = useState<PostState>("posting");
  const [postedComment, setPostedComment] = useState<string | null>(null);

  const date = new Date(dateStr + "T12:00:00").toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long" });
  const totalLoad = activities.reduce((sum, a) => sum + (a.icu_training_load ?? 0), 0);
  const main = activities.reduce((best, a) =>
    (a.icu_training_load ?? 0) >= (best.icu_training_load ?? 0) ? a : best, activities[0]);

  useEffect(() => {
    const comment = generateDayComment(activities, wellness);
    onPost(main.id, comment)
      .then(() => { setPostedComment(comment); setPostState("done"); })
      .catch(() => setPostState("error"));
  }, []);

  return (
    <div className="cal__overlay" onClick={onClose}>
      <div className="cal__modal" onClick={e => e.stopPropagation()}>
        <button className="cal__modal-close" onClick={onClose}>✕</button>
        <p className="cal__modal-date">{date}</p>
        <h3 className="cal__modal-title">Dagssummering · Load {totalLoad}</h3>
        <div className="cal__modal-desc">
          {activities.map((a, i) => (
            <div key={i} style={{ marginBottom: i < activities.length - 1 ? "0.3rem" : 0 }}>
              {icon(a.type)} {a.name}
              {a.icu_training_load != null && ` · Load ${a.icu_training_load}`}
              {a.average_heartrate != null && ` · ${a.average_heartrate} bpm`}
            </div>
          ))}
        </div>

        {postState === "posting" && (
          <p className="cal__modal-analyze-status">Analyserar och postar…</p>
        )}
        {postState === "done" && postedComment && (
          <div className="cal__modal-analyze-result">
            <span className="cal__modal-analyze-ok">✓ Postad på {main.name}</span>
            <p>{postedComment}</p>
          </div>
        )}
        {postState === "error" && (
          <p className="cal__modal-analyze-status cal__modal-analyze-status--error">
            Kunde inte posta kommentar — försök igen.
          </p>
        )}
      </div>
    </div>
  );
}

interface DaySelection { dateStr: string; activities: Activity[]; wellness: WellnessDay | null; }

const ADD_SPORTS: { value: SavedExercise["sport"]; label: string }[] = [
  { value: "Run", label: "Löpning" },
  { value: "Ride", label: "Cykel" },
  { value: "NordicSki", label: "Skidor" },
  { value: "RollerSki", label: "Rullskidor" },
  { value: "WeightTraining", label: "Gym" },
];

function AddExerciseModal({ dateStr, onClose, onAdd }: {
  dateStr: string;
  onClose: () => void;
  onAdd: (payload: NewEventPayload) => Promise<void>;
}) {
  const [list, setList] = useState<SavedExercise[]>(() => loadSavedExercises());
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [sport, setSport] = useState<SavedExercise["sport"]>("Ride");
  const [duration, setDuration] = useState("55");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const prettyDate = new Date(dateStr + "T12:00:00").toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long" });

  async function add(ex: SavedExercise) {
    setBusy(true);
    try { await onAdd(exerciseToEventPayload(ex, dateStr)); onClose(); }
    finally { setBusy(false); }
  }

  async function saveAndAdd() {
    const min = parseInt(duration, 10);
    if (!name.trim() || !min) return;
    const description = desc.trim() || `${min}min Z2 HR`;
    const next = addSavedExercise({ name: name.trim(), sport, durationMin: min, description });
    setList(next);
    const created = next[next.length - 1];
    await add(created);
  }

  return (
    <div className="cal__modal-backdrop" onClick={onClose}>
      <div className="cal__modal" onClick={e => e.stopPropagation()}>
        <div className="cal__modal-status">Lägg till pass</div>
        <div className="cal__modal-date">{prettyDate}</div>

        <div className="cal__ex-list">
          {list.map(ex => (
            <div key={ex.id} className="cal__ex-row">
              <button className="cal__ex-add" disabled={busy} onClick={() => add(ex)}>
                <span className="cal__ex-name">{ex.name}</span>
                <span className="cal__ex-meta">{ADD_SPORTS.find(s => s.value === ex.sport)?.label ?? ex.sport} · {ex.durationMin} min</span>
              </button>
              <button className="cal__ex-del" title="Ta bort" onClick={() => setList(removeSavedExercise(ex.id))}>✕</button>
            </div>
          ))}
        </div>

        {showNew ? (
          <div className="cal__ex-form">
            <input className="cal__ex-input" placeholder="Namn (t.ex. Spinning)" value={name} onChange={e => setName(e.target.value)} />
            <div className="cal__ex-form-row">
              <select className="cal__ex-input" value={sport} onChange={e => setSport(e.target.value as SavedExercise["sport"])}>
                {ADD_SPORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <input className="cal__ex-input cal__ex-input--min" type="number" min={1} placeholder="min" value={duration} onChange={e => setDuration(e.target.value)} />
            </div>
            <input className="cal__ex-input" placeholder="Beskrivning (valfri, t.ex. 55min Z2 HR)" value={desc} onChange={e => setDesc(e.target.value)} />
            <button className="cal__ex-save" disabled={busy || !name.trim() || !parseInt(duration, 10)} onClick={saveAndAdd}>
              Spara & lägg till
            </button>
          </div>
        ) : (
          <button className="cal__ex-new-toggle" onClick={() => setShowNew(true)}>+ Ny övning</button>
        )}

        <button className="cal__modal-close" onClick={onClose}>Stäng</button>
      </div>
    </div>
  );
}

function DayModal({ dateStr, activities, events, onClose, onAdd, onOpenActivity, onOpenEvent, onDelete, onMarkSick }: {
  dateStr: string;
  activities: Activity[];
  events: CalendarEvent[];
  onClose: () => void;
  onAdd: (payload: NewEventPayload) => Promise<void>;
  onOpenActivity: (a: Activity) => void;
  onOpenEvent: (e: CalendarEvent) => void;
  onDelete: (id: number) => Promise<void>;
  onMarkSick: (dateStr: string) => Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmSick, setConfirmSick] = useState(false);
  const [sickBusy, setSickBusy] = useState(false);
  const prettyDate = new Date(dateStr + "T12:00:00").toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long" });
  const isTodayOrPast = dateStr <= new Date().toISOString().slice(0, 10);

  async function doDelete(id: number) {
    setDeletingId(id);
    try { await onDelete(id); } finally { setDeletingId(null); setConfirmDelete(null); }
  }
  async function doMarkSick() {
    setSickBusy(true);
    try { await onMarkSick(dateStr); onClose(); } finally { setSickBusy(false); }
  }

  // The "+" opens the saved-exercise picker in place; adding closes the whole flow.
  if (adding) {
    return (
      <AddExerciseModal
        dateStr={dateStr}
        onClose={() => setAdding(false)}
        onAdd={async (p) => { await onAdd(p); setAdding(false); onClose(); }}
      />
    );
  }

  return (
    <div className="cal__modal-backdrop" onClick={onClose}>
      <div className="cal__modal" onClick={e => e.stopPropagation()}>
        <div className="cal__modal-status">Dagens pass</div>
        <div className="cal__modal-name" style={{ textTransform: "capitalize" }}>{prettyDate}</div>

        <div className="cal__day-sec-title">Genomfört</div>
        {activities.length > 0 ? activities.map((a, i) => (
          <button key={i} className="cal__day-item cal__day-item--done" onClick={() => onOpenActivity(a)}>
            <span className="cal__day-item-name">{icon(a.type)} {a.name}</span>
            <span className="cal__day-item-meta">{fmtDuration(a.moving_time)}{a.icu_training_load ? ` · ${a.icu_training_load} TSS` : ""}</span>
          </button>
        )) : <div className="cal__day-empty">Inget genomfört.</div>}

        <div className="cal__day-sec-title">Planerat</div>
        {events.length > 0 ? events.map((e, i) => (
          <div key={i} className="cal__day-item-row">
            <button className="cal__day-item cal__day-item--planned" onClick={() => onOpenEvent(e)}>
              <span className="cal__day-item-name">{isRaceEvent(e) ? "🏁 " : ""}{e.name}</span>
            </button>
            {!isRaceEvent(e) && (
              confirmDelete === e.id ? (
                <button className="cal__day-del cal__day-del--confirm" disabled={deletingId === e.id}
                  onClick={() => doDelete(e.id)}>
                  {deletingId === e.id ? "…" : "Ta bort?"}
                </button>
              ) : (
                <button className="cal__day-del" title="Ta bort pass" onClick={() => setConfirmDelete(e.id)}>✕</button>
              )
            )}
          </div>
        )) : <div className="cal__day-empty">Inget planerat.</div>}

        <button className="cal__ex-new-toggle" onClick={() => setAdding(true)}>+ Lägg till pass</button>

        {isTodayOrPast && (
          confirmSick ? (
            <div className="cal__day-sick-confirm">
              <p>Markera denna dag som sjukdag? Passen tas inte bort — de markeras som vila (röda).</p>
              <div className="cal__day-sick-actions">
                <button className="cal__day-sick-btn cal__day-sick-btn--danger" disabled={sickBusy} onClick={doMarkSick}>
                  {sickBusy ? "…" : "Ja, sjuk"}
                </button>
                <button className="cal__day-sick-btn" onClick={() => setConfirmSick(false)}>Avbryt</button>
              </div>
            </div>
          ) : (
            <button className="cal__day-sick" onClick={() => setConfirmSick(true)}>🤒 Sjuk denna dag</button>
          )
        )}

        <button className="cal__modal-close" onClick={onClose}>Stäng</button>
      </div>
    </div>
  );
}

function Calendar({ events, activities, ctl, atl, wellnessHistory, deleteEvent, postMessage, updateEvent, editEventDescription, onCreateEvent, onMarkSick, sickSince }: {
  events: CalendarEvent[];
  activities: Activity[];
  ctl: number | null;
  atl: number | null;
  wellnessHistory: WellnessDay[];
  deleteEvent: (id: number) => Promise<void>;
  postMessage: (activityId: string, content: string) => Promise<void>;
  updateEvent: (id: number, newDateLocal: string) => Promise<void>;
  editEventDescription: (id: number, description: string) => Promise<void>;
  onCreateEvent: (payload: NewEventPayload) => Promise<void>;
  onMarkSick: (dateStr: string) => Promise<void>;
  sickSince: string | null;
}) {
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [selectedAdvice, setSelectedAdvice] = useState<EventAdvice | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [selectedDay, setSelectedDay] = useState<DaySelection | null>(null);
  const [dayModalDate, setDayModalDate] = useState<string | null>(null); // day the Day window targets

  // Drag-and-drop state
  const [localEvents, setLocalEvents] = useState<CalendarEvent[]>(events);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dragOverDay, setDragOverDay] = useState<string | null>(null);
  const [dragError, setDragError] = useState<string | null>(null);
  const eventsRef = useRef(events);
  const touchDragRef = useRef<{ id: number; ghost: HTMLElement | null; lastDay: string | null } | null>(null);

  // Keep localEvents in sync when props change (e.g. after delete)
  useEffect(() => {
    eventsRef.current = events;
    setLocalEvents(events);
  }, [events]);

  const tsb = ctl !== null && atl !== null ? ctl - atl : null;

  const wellnessByDay: Record<string, WellnessDay> = {};
  for (const w of wellnessHistory) wellnessByDay[w.id] = w;

  const weekIntense = localEvents
    .filter(e => { const d = daysFromNow(e.start_date_local); return d >= 0 && d < 7 && isIntense(e); })
    .sort((a, b) => a.start_date_local.localeCompare(b.start_date_local));

  function getAdvice(event: CalendarEvent): EventAdvice | null {
    if (isRaceEvent(event)) return null;
    // While sick, every workout from the sick onset through today is flagged red
    // as suggested rest (not deleted) — the athlete decides whether to skip it.
    const d = event.start_date_local.slice(0, 10);
    if (sickSince && d >= sickSince && d <= todayStr) {
      return { verdict: "remove", reason: "Sjukdag – vila och låt kroppen återhämta sig. Hoppa över eller ta bort passet." };
    }
    if (tsb === null) return null;
    const days = daysFromNow(event.start_date_local);
    if (days < 0 || days >= 7) return null;
    return computeAdvice(event, tsb, weekIntense.length, weekIntense.indexOf(event));
  }

  // Per-week plan summary (phase / planned hours / hard%) the planner persisted.
  const planMeta = loadPlanWeekMeta();

  // Build lookup from localEvents so optimistic moves are reflected immediately
  const eventsByDay: Record<string, CalendarEvent[]> = {};
  for (const e of localEvents) {
    const day = e.start_date_local.slice(0, 10);
    if (!eventsByDay[day]) eventsByDay[day] = [];
    eventsByDay[day].push(e);
  }

  const activitiesByDay: Record<string, Activity[]> = {};
  for (const a of activities) {
    const day = a.start_date_local.slice(0, 10);
    if (!activitiesByDay[day]) activitiesByDay[day] = [];
    activitiesByDay[day].push(a);
  }

  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;

  const prev = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const next = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  function handleEventTouchStart(e: React.TouchEvent, eventId: number) {
    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    let dragging = false;
    let ghost: HTMLElement | null = null;

    function onMove(ev: TouchEvent) {
      const t = ev.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;

      if (!dragging && Math.sqrt(dx * dx + dy * dy) > 8) {
        dragging = true;
        setDraggingId(eventId);
        ghost = target.cloneNode(true) as HTMLElement;
        ghost.style.cssText = `position:fixed;left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;opacity:0.8;pointer-events:none;z-index:9999;transform:scale(1.1);transition:none;`;
        document.body.appendChild(ghost);
        touchDragRef.current = { id: eventId, ghost, lastDay: null };
      }

      if (!dragging || !touchDragRef.current) return;
      ev.preventDefault();

      if (ghost) {
        ghost.style.left = `${t.clientX - rect.width / 2}px`;
        ghost.style.top  = `${t.clientY - 14}px`;
      }

      const el = document.elementFromPoint(t.clientX, t.clientY);
      const cell = el?.closest('[data-date]') as HTMLElement | null;
      const day = cell?.dataset.date ?? null;
      if (day !== touchDragRef.current.lastDay) {
        touchDragRef.current.lastDay = day;
        setDragOverDay(day);
      }
    }

    function onEnd() {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      if (!dragging) return;
      if (ghost) { try { document.body.removeChild(ghost); } catch {} }
      const targetDay = touchDragRef.current?.lastDay ?? null;
      touchDragRef.current = null;
      if (targetDay) {
        performMove(eventId, targetDay); // use eventId from outer closure — avoids stale state
      } else {
        setDraggingId(null);
        setDragOverDay(null);
      }
    }

    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  }

  const performMove = async (eventId: number, targetDay: string) => {
    const ev = localEvents.find(e => e.id === eventId);
    if (!ev || ev.start_date_local.slice(0, 10) === targetDay) {
      setDraggingId(null);
      setDragOverDay(null);
      return;
    }
    const existingTime = ev.start_date_local.slice(10) || "T00:00:00";
    const newStart = targetDay + existingTime;
    setLocalEvents(prev => prev.map(e => e.id === eventId ? { ...e, start_date_local: newStart } : e));
    setDraggingId(null);
    setDragOverDay(null);
    try {
      await updateEvent(eventId, newStart);
    } catch {
      setLocalEvents(eventsRef.current);
      setDragError("Kunde inte flytta passet. Försök igen.");
      setTimeout(() => setDragError(null), 3000);
    }
  };

  const handleDrop = (targetDay: string) => {
    if (draggingId === null) return;
    performMove(draggingId, targetDay);
  };

  return (
    <div className="cal">
      {selectedEvent && (
        <EventModal
          event={selectedEvent}
          advice={selectedAdvice}
          onClose={() => { setSelectedEvent(null); setSelectedAdvice(null); }}
          onDelete={deleteEvent}
          onEditDescription={editEventDescription}
        />
      )}
      {selectedActivity && (
        <ActivityModal
          activity={selectedActivity}
          onClose={() => setSelectedActivity(null)}
        />
      )}
      {selectedDay && (
        <DayAnalysisModal
          dateStr={selectedDay.dateStr}
          activities={selectedDay.activities}
          wellness={selectedDay.wellness}
          onClose={() => setSelectedDay(null)}
          onPost={postMessage}
        />
      )}

      {dayModalDate && (
        <DayModal
          dateStr={dayModalDate}
          activities={activitiesByDay[dayModalDate] ?? []}
          events={localEvents.filter(e => e.start_date_local.slice(0, 10) === dayModalDate)}
          onClose={() => setDayModalDate(null)}
          onAdd={onCreateEvent}
          onOpenActivity={(a) => { setDayModalDate(null); setSelectedActivity(a); }}
          onOpenEvent={(e) => { setDayModalDate(null); setSelectedEvent(e); setSelectedAdvice(getAdvice(e)); }}
          onDelete={deleteEvent}
          onMarkSick={onMarkSick}
        />
      )}

      {dragError && <div className="cal__drag-error">{dragError}</div>}

      <div className="cal__nav">
        <button className="cal__nav-btn" onClick={prev}>‹</button>
        <span className="cal__month">{MONTHS[month]} {year}</span>
        <button className="cal__nav-btn" onClick={next}>›</button>
      </div>

      <div className="cal__legend">
        <span className="cal__legend-item cal__legend-item--activity">Genomfört</span>
        <span className="cal__legend-item cal__legend-item--event">Planerat</span>
        <span className="cal__legend-item cal__legend-item--race">Tävling</span>
        <span className="cal__legend-item cal__legend-item--move">Flytta</span>
        <span className="cal__legend-item cal__legend-item--remove">Ta bort</span>
        <span className="cal__legend-sep" />
        <span className="cal__legend-item cal__legend-item--low">Load &lt;{Math.round((ctl ?? 50) * 0.75)}</span>
        <span className="cal__legend-item cal__legend-item--medium">{Math.round((ctl ?? 50) * 0.75)}–{Math.round((ctl ?? 50) * 1.5)}</span>
        <span className="cal__legend-item cal__legend-item--high">&gt;{Math.round((ctl ?? 50) * 1.5)}</span>
      </div>

      <div className="cal__grid">
        <div className="cal__dow cal__dow--wk" />
        {DAYS.map(d => <div key={d} className="cal__dow">{d}</div>)}
        {Array.from({ length: totalCells / 7 }).flatMap((_, r) => {
          // Whole Mon–Sun week (incl. days spilling into adjacent months) so the
          // weekly volume / hard% reflect the real training week.
          const weekDates = Array.from({ length: 7 }, (_, c) =>
            new Date(year, month, 1 + (r * 7 + c - startOffset)));
          const fmtLocal = (dt: Date) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
          const weekActs = weekDates.flatMap(dt => activitiesByDay[fmtLocal(dt)] ?? []);
          const m = weeklyMetrics(weekActs);
          const wkNum = isoWeekNum(fmtLocal(weekDates[0]));
          const meta = planMeta[isoWeekKey(fmtLocal(weekDates[0]))]; // plan target for this week, if any
          // A week not yet started shows only the PLAN target (no "0m" — you
          // haven't trained it yet). Started weeks show achieved vs planned; a
          // fully-elapsed week is colored hit/miss so you can review adherence.
          const isFutureWeek = fmtLocal(weekDates[0]) > todayStr;
          const isPastWeek = fmtLocal(weekDates[6]) < todayStr;
          const hasContent = m.secs > 0 || meta != null;
          const hitClass = isPastWeek && meta
            ? (m.secs >= meta.hours * 3600 * 0.9 ? " cal__wk-vol--hit" : " cal__wk-vol--miss")
            : "";
          const weekCell = (
            <div key={`wk-${r}`} className="cal__week-cell">
              <span className="cal__wk-num">v{wkNum}</span>
              {meta && <span className={`cal__wk-phase cal__wk-phase--${meta.phase}`}>{PHASE_LABELS[meta.phase] ?? meta.phase}</span>}
              {isFutureWeek && meta ? (
                <span className="cal__wk-vol cal__wk-vol--plan" title="Planerat">{fmtHours(meta.hours)}</span>
              ) : hasContent ? (
                <>
                  <span className={`cal__wk-vol${hitClass}`} title="Genomfört">{m.secs > 0 ? fmtDuration(m.secs) : "0m"}</span>
                  {meta && <span className="cal__wk-planned" title="Planerat">/ {fmtHours(meta.hours)}</span>}
                </>
              ) : (
                <span className="cal__wk-vol cal__wk-vol--empty">—</span>
              )}
              {isFutureWeek && meta ? (
                <span className="cal__wk-hard" title="Planerad andel i Z4+">{meta.hardPct}%</span>
              ) : !isFutureWeek && m.hardPct != null && m.hardPct > 0 ? (
                <span className="cal__wk-hard" title="Genomförd andel i Z4+ (snittpuls ≥ 90% av tröskelpuls)">{m.hardPct}%</span>
              ) : meta ? (
                <span className="cal__wk-hard" title="Planerad andel i Z4+">{meta.hardPct}%</span>
              ) : null}
            </div>
          );
          const dayCells = Array.from({ length: 7 }, (_, c) => {
            const i = r * 7 + c;
            const dayNum = i - startOffset + 1;
            if (dayNum < 1 || dayNum > daysInMonth) return <div key={i} className="cal__cell cal__cell--empty" />;
            const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
          const dayEvents = eventsByDay[dateStr] ?? [];
          const dayActivities = activitiesByDay[dateStr] ?? [];
          const isToday = dateStr === todayStr;
          const isDragTarget = dragOverDay === dateStr;
          const hasContent = dayEvents.length > 0 || dayActivities.length > 0;
          const dailyLoad = dayActivities.reduce((sum, a) => sum + (a.icu_training_load ?? 0), 0) || null;
          const ref = ctl ?? 50;
          const loadLevel = dailyLoad === null ? null : dailyLoad >= ref * 1.5 ? "high" : dailyLoad >= ref * 0.75 ? "medium" : "low";
          return (
            <div
              key={i}
              data-date={dateStr}
              className={`cal__cell${isToday ? " cal__cell--today" : ""}${hasContent ? " cal__cell--has-events" : ""}${isDragTarget ? " cal__cell--drag-over" : ""}`}
              onClick={() => setDayModalDate(dateStr)}
              onDragOver={e => { e.preventDefault(); setDragOverDay(dateStr); }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverDay(null); }}
              onDrop={() => handleDrop(dateStr)}
            >
              <div className="cal__day-header">
                <span className="cal__day-num">{dayNum}</span>
                {dailyLoad !== null && (
                  <span
                    className={`cal__day-load cal__day-load--${loadLevel} cal__day-load--btn`}
                    onClick={e => { e.stopPropagation(); setSelectedDay({ dateStr, activities: dayActivities, wellness: wellnessByDay[dateStr] ?? null }); }}
                    title="Analysera dagen"
                  >
                    {dailyLoad}
                  </span>
                )}
              </div>
              {dayActivities.map((a, j) => (
                <span key={j} className="cal__activity-chip">{a.name}</span>
              ))}
              {dayEvents.map((e, j) => {
                const isRace = isRaceEvent(e);
                const advice = isRace ? null : getAdvice(e);
                const isFutureEvent = e.start_date_local.slice(0, 10) >= todayStr;
                const isDragging = draggingId === e.id;
                const mod = isRace ? " cal__event--race"
                  : advice?.verdict === "remove" ? " cal__event--remove"
                  : advice?.verdict === "move" ? " cal__event--move"
                  : "";
                return (
                  <span
                    key={j}
                    className={`cal__event${mod}${isDragging ? " cal__event--dragging" : ""}`}
                    draggable={isFutureEvent}
                    onDragStart={isFutureEvent ? () => setDraggingId(e.id) : undefined}
                    onDragEnd={() => { setDraggingId(null); setDragOverDay(null); }}
                    onTouchStart={isFutureEvent ? (ev) => handleEventTouchStart(ev, e.id) : undefined}
                    style={isFutureEvent ? { cursor: isDragging ? "grabbing" : "grab", touchAction: "none" } : undefined}
                  >
                    {e.name}
                  </span>
                );
              })}
            </div>
            );
          });
          return [weekCell, ...dayCells];
        })}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

interface TrainingPageViewProps {
  data: TrainingData | null;
  deleteEvent: (id: number) => Promise<void>;
  updateEvent: (id: number, newDateLocal: string) => Promise<void>;
  editEventDescription: (id: number, description: string) => Promise<void>;
  sickSince: string | null;
  onMarkSick: (dateStr: string) => Promise<void>;
  onEndSick: () => Promise<void>;
  postActivityMessage: (activityId: string, content: string) => Promise<void>;
  onCreateEvent: (payload: NewEventPayload) => Promise<void>;
}

export const TrainingPageView = ({ data, deleteEvent, updateEvent, editEventDescription, sickSince, onMarkSick, onEndSick, postActivityMessage, onCreateEvent }: TrainingPageViewProps) => {
  usePageBackground();
  const history = useHistory();
  const loading = !data;
  const wellness = data?.wellness;
  const activities = data?.activities ?? [];
  const events = data?.events ?? [];
  const wHistory = data?.wellnessHistory ?? [];

  // Use last 10 entries for charts, full history for wellness-by-day lookup in calendar
  const chartHistory = wHistory.slice(-10);
  const dates   = chartHistory.map((d: WellnessDay) => d.id);
  const ctlVals = chartHistory.map((d: WellnessDay) => d.ctl);
  const atlVals = chartHistory.map((d: WellnessDay) => d.atl);
  const formVals = chartHistory.map((d: WellnessDay) =>
    d.ctl !== null && d.atl !== null ? d.ctl - d.atl : null
  );
  const hrvVals = chartHistory.map((d: WellnessDay) => d.hrv);
  const rhrVals = chartHistory.map((d: WellnessDay) => d.restingHR);

  const [sickMsg, setSickMsg] = useState<string | null>(null);
  const [sickBusy, setSickBusy] = useState(false);
  // Mark a specific day sick (from its Day window): record sickSince so the plan
  // ramps back on recovery. Workouts are NOT deleted — they stay flagged red as
  // suggested rest. Krya på dig!
  async function markSickDay(dateStr: string) {
    await onMarkSick(dateStr);
    setSickMsg(`Sjukdag noterad. Passen är markerade som vila (röda) — hoppa över eller ta bort dem. Tryck "Frisk igen" när du är tillbaka.`);
    setTimeout(() => setSickMsg(null), 8000);
  }
  async function handleEndSick() {
    setSickBusy(true);
    try {
      await onEndSick();
      setSickMsg("Välkommen tillbaka! Planen trappar upp volymen gradvis de närmsta veckorna.");
    } finally { setSickBusy(false); }
    setTimeout(() => setSickMsg(null), 8000);
  }

  return (
    <div className="training">
      <div className="training__header">
        <NavLink to={routeUrls.HOME} className="training__back">← Hem</NavLink>
        <h1 className="training__title">Träning</h1>
        <div className="training__header-right">
          <NavLink to={routeUrls.TRAINING_HISTORY} className="training__link">
            Översikt 3 år →
          </NavLink>
          <NavLink to={routeUrls.TRAINING_PLAN} className="training__link">
            Planera →
          </NavLink>
          <a
            href={`https://intervals.icu/athlete/${process.env.REACT_APP_INTERVALS_ATHLETE_ID}/calendar`}
            target="_blank" rel="noopener noreferrer"
            className="training__link"
          >
            intervals.icu ↗
          </a>
        </div>
      </div>

      {sickMsg && <div className="training__sick-msg">{sickMsg}</div>}

      <div className="training__logout-row">
        <button className="training__logout-btn" onClick={() => auth.signOut().then(() => history.push(routeUrls.HOME))}>
          Logga ut
        </button>
      </div>

      {loading ? (
        <p className="training__empty">Laddar…</p>
      ) : (
        <>
          {chartHistory.length >= 2 && (
            <div className="metric-charts">
              <MetricChart label="Fitness (CTL)" tooltipTitle="Fitness (CTL)" tooltipText="42-day weighted average of training load. Recreational athletes typically range 30–80, elites can exceed 150. Higher means more fitness built up over time." values={ctlVals} dates={dates} />
              <MetricChart label="Fatigue (ATL)" tooltipTitle="Fatigue (ATL)" tooltipText="7-day weighted average of training load. Reflects how hard you've trained recently. Higher than your Fitness means you're in a hard training block." values={atlVals} dates={dates} />
              <MetricChart label="Form (TSB)" tooltipTitle="Form (TSB = Fitness − Fatigue)" tooltipText="Negative = fatigued, in a training block. Around 0 = balanced. +5 to +15 = fresh and race-ready. Very positive means well-rested but potentially losing fitness." values={formVals} dates={dates} signed colorize />
              <MetricChart label="HRV" tooltipTitle="Heart Rate Variability" tooltipText="Higher generally means better recovery and readiness. Track the trend — a dip below your baseline can signal fatigue or illness before you feel it." values={hrvVals} dates={dates} />
              <MetricChart label="Resting HR" tooltipTitle="Resting Heart Rate" tooltipText="Lower generally indicates better cardiovascular fitness. A spike of 5+ bpm above your normal can indicate accumulated fatigue or incoming illness." values={rhrVals} dates={dates} />
            </div>
          )}

          <section className="training__section training__section--full">
            <div className="training__cal-head">
              <h2 className="training__section-title">Träningskalender</h2>
              <div className="training__sick-controls">
                {sickSince && (
                  <button className="training__sick-btn training__sick-btn--active" disabled={sickBusy} onClick={handleEndSick}>
                    💪 Frisk igen
                  </button>
                )}
              </div>
            </div>
            <Calendar
              events={events}
              activities={activities}
              ctl={wellness?.ctl ?? null}
              atl={wellness?.atl ?? null}
              wellnessHistory={wHistory}
              deleteEvent={deleteEvent}
              updateEvent={updateEvent}
              editEventDescription={editEventDescription}
              postMessage={postActivityMessage}
              onCreateEvent={onCreateEvent}
              onMarkSick={markSickDay}
              sickSince={sickSince}
            />
          </section>

          <section className="training__section training__section--full">
            <h2 className="training__section-title">Veckans TSS</h2>
            <WeeklyTSSChart activities={activities} events={events} />
          </section>

          <section className="training__section training__section--full">
            <h2 className="training__section-title">Träningsintensitet (±8 veckor)</h2>
            {wellness && <PolarizationChart activities={activities} events={events} wellness={wellness} />}
          </section>

          <section className="training__section training__section--full">
            <TrainingInfo />
          </section>
        </>
      )}
    </div>
  );
};
